import { useState, useEffect } from "react";
import SquadBuildr from "./SquadBuilder";

/* ══════════ CONFIG ══════════ */
const CLUB      = "AC Milan";
const SEASON    = "2026/27";
const STORE_KEY = "milan_rosa_2627_v2";
const CURR_YEAR = 2027;

const TABS = [
  { id:"dettaglio", label:"DETTAGLIO ROSA" },
  { id:"serieA",    label:"LISTA SERIE A"  },
  { id:"ucl",       label:"LISTA UEFA"     },
  { id:"mercato",   label:"MERCATO"        },
  { id:"squad",     label:"SQUAD BUILDER"   },
];

const CAT_SA = [
  { id:"libero",       label:"Over",          color:"#6B8DD6", max:17,   min:null },
  { id:"vivaioClub",   label:"Vivaio Club",    color:"#7B9FD4", max:4,    min:null },
  { id:"vivaioItalia", label:"Vivaio Italia",  color:"#4AADA8", max:4,    min:null },
  { id:"under22",      label:"U22",            color:"#C4A05A", max:null, min:null },
  { id:"fuoriLista",   label:"Fuori Lista",    color:"#C06060", max:null, min:null },
];

const CAT_UEFA = [
  { id:"liberoUEFA",     label:"Non Formato",   color:"#6B8DD6", max:17,   min:null },
  { id:"clubUEFA",       label:"Vivaio Club",    color:"#7B9FD4", max:4,    min:null },
  { id:"assocUEFA",      label:"Vivaio Italia",  color:"#4AADA8", max:4,    min:null },
  { id:"listaBUEFA",     label:"Lista B",        color:"#C4A05A", max:null, min:null },
  { id:"fuoriListaUEFA", label:"Fuori Lista",    color:"#C06060", max:null, min:null },
];

const ALL_CATS = [...CAT_SA, ...CAT_UEFA];
const getCat = (id) => ALL_CATS.find(c => c.id === id) ?? ALL_CATS[0];

const ROLES = ["POR","DIF","CEN","EST","TRQ","ATT"];
const RC = { POR:"#C4A05A", DIF:"#6B8DD6", CEN:"#8B7FCC", EST:"#4AADA8", TRQ:"#A57CC4", ATT:"#B86A6A" };
const RL = { POR:"Portiere", DIF:"Difensore", CEN:"Centrocampista", EST:"Esterno", TRQ:"Trequartista", ATT:"Attaccante" };

const M = {
  bg:"#0D1119", surface:"#111827", raised:"#18212F", hover:"#1D2A3A",
  border:"#253447", borderS:"#1A2536",
  txt:"#E4EAF4", txt2:"#7A8FA8", txt3:"#3A4E64",
  acq:"#3DB88A", blue:"#6B8DD6", amber:"#C4A05A", red:"#C06060",
};
const CONTENT_FRAME_STYLE = {
  display:"flex", width:"100%", maxWidth:1920, boxSizing:"border-box",
  margin:"0 auto", padding:"18px 22px", gap:16, alignItems:"flex-start",
};

/* ══════════ STORAGE ══════════ */
const store = window.storage ? window.storage : {
  get: async (k) => { try{ const v=localStorage.getItem(k); return v?{value:v}:null; }catch{ return null; } },
  set: async (k,v) => { try{ localStorage.setItem(k,v); }catch{} },
};

/* ══════════ DATA NORMALIZATION ══════════ */
const parseBirthDate = (v) => {
  if (!v) return "";
  const [d,m,y] = v.split("/");
  return y ? `${y}-${m}-${d}` : v;
};
const isUnder22 = (ddn) => ddn >= "2004-01-01";

const normalizePlayer = (p, i) => {
  const ddn  = parseBirthDate(p["data-di-nascita"]);
  const u22  = isUnder22(ddn);
  const fL   = p["fuori-lista"] === true;
  const fLUE = p["fuori-lista-uefa"] === true;
  const rRaw = p.ruolo === "DEF" ? "DIF" : p.ruolo;
  const ruolo = ROLES.includes(rRaw) ? rRaw : "ATT";
  return {
    id:           String(p.id ?? i),
    nome:         p.nome.trim(),
    ruolo,
    naz:          String(p["nazionalità"] ?? "").trim().toUpperCase(),
    numeroMaglia: p["numero-maglia"] ?? "",
    ddn,
    piede:        p["piede"] ?? "DX",
    altezza:      p["altezza"] ?? null,
    inRosaDa:     p["in-rosa-da"] ?? "",
    vivaio:       !p["in-rosa-da"],
    compratoA:    p["comprato-a"] ?? 0,
    vivaioClub:   p["vivaio-club"]   === true,
    vivaioItalia: p["vivaio-italia"] === true,
    cat:      fL   ? "fuoriLista"     : u22 ? "under22"     : p["vivaio-club"]   ? "vivaioClub"   : p["vivaio-italia"] ? "vivaioItalia" : "libero",
    catUEFA:  fLUE ? "fuoriListaUEFA" : p["lista-b-uefa"] ? "listaBUEFA" : p["club-uefa"] ? "clubUEFA" : p["assoc-uefa"] ? "assocUEFA" : "liberoUEFA",
    contratto:    p.contratto ?? CURR_YEAR + 1,
    acq:          p["in-rosa-da"] === String(CURR_YEAR - 1),
    inVendita:    p["in-vendita"] === true,
    ignora:       p.ignora === true,
    imgUrl:       p.immagine ?? "",
  };
};

/* ══════════ UTILS ══════════ */
const calcAge = (d) => {
  if (!d) return "–";
  const t=new Date(), b=new Date(d);
  let a=t.getFullYear()-b.getFullYear();
  if (t.getMonth()<b.getMonth()||(t.getMonth()===b.getMonth()&&t.getDate()<b.getDate())) a--;
  return a;
};
const initials   = (n) => { const p=n.trim().split(/\s+/); return (p.length>=2?p[0][0]+p[p.length-1][0]:n.slice(0,2)).toUpperCase(); };
const sortByRole = (arr) => [...arr].sort((a,b)=>ROLES.indexOf(a.ruolo)-ROLES.indexOf(b.ruolo)||a.nome.localeCompare(b.nome));
const avg        = (arr) => arr.length ? arr.reduce((a,b)=>a+b,0)/arr.length : 0;
const fmt1       = (n)   => isNaN(n)||n==null ? "—" : Number(n).toFixed(1);
const fmt2       = (n)   => isNaN(n)||n==null ? "—" : Number(n).toFixed(2);
const loyaltyYrs = (s)   => { if (!s) return null; const y=parseFloat(s); return isNaN(y)?null:Math.round((CURR_YEAR-y)*10)/10; };

/* ══════════ AVATAR ══════════ */
function Avatar({ p, size=34 }) {
  const [err, setErr] = useState(false);
  const bg = { POR:"#1E1A0E",DIF:"#0E1320",CEN:"#120F1E",EST:"#0C1A1A",TRQ:"#160E20",ATT:"#1E0F0F" };
  if (p.imgUrl && !err) return (
    <div style={{width:size,height:size,borderRadius:"50%",flexShrink:0,overflow:"hidden",border:`1px solid ${M.border}`,background:bg[p.ruolo]??M.bg}}>
      <img src={p.imgUrl} alt={p.nome} onError={()=>setErr(true)} style={{width:"100%",height:"100%",objectFit:"cover",objectPosition:"top center"}} />
    </div>
  );
  return (
    <div style={{width:size,height:size,borderRadius:"50%",flexShrink:0,background:bg[p.ruolo]??M.bg,border:`1px solid ${M.borderS}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:size*0.27,fontWeight:700,color:RC[p.ruolo]??M.txt3}}>
      {initials(p.nome)}
    </div>
  );
}

/* ══════════ ROLE BADGE ══════════ */
function RoleBadge({ ruolo }) {
  const color = RC[ruolo] ?? M.txt3;
  return <span style={{fontSize:9,fontWeight:700,letterSpacing:".06em",color,background:`${color}14`,border:`1px solid ${color}30`,borderRadius:4,padding:"1px 5px",flexShrink:0,fontFamily:"inherit"}}>{ruolo}</span>;
}

/* ══════════ SMALL TAG ══════════ */
function Tag({ label, color }) {
  return <span style={{fontSize:8.5,fontWeight:700,letterSpacing:".06em",color,background:`${color}14`,border:`1px solid ${color}30`,borderRadius:4,padding:"1px 5px",flexShrink:0,fontFamily:"inherit"}}>{label}</span>;
}

/* ══════════ PLAYER ROW (list views) ══════════ */
function PlayerRow({ p, onView, compact=false }) {
  const [hov, setHov] = useState(false);
  const expiring     = p.contratto <= CURR_YEAR;
  const expiringNext = p.contratto === CURR_YEAR + 1;
  const contractColor = expiring ? M.amber : expiringNext ? M.blue : M.txt3;
  const bgBase = compact ? M.surface : p.inVendita ? `${M.red}18` : p.acq ? `${M.acq}18` : M.surface;
  const bgHov  = compact ? M.hover   : p.inVendita ? `${M.red}24` : p.acq ? `${M.acq}24` : M.hover;
  return (
    <div
      onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      onClick={()=>onView(p)}
      style={{
        display:"flex",alignItems:"center",gap:8, cursor:"pointer",
        background:hov?bgHov:bgBase, borderRadius:8,
        borderTop:`1px solid ${M.borderS}`, borderRight:`1px solid ${M.borderS}`,
        borderBottom:`1px solid ${M.borderS}`, borderLeft:`1px solid ${M.borderS}`,
        padding:"7px 9px", transition:"background .12s",
      }}
    >
      <RoleBadge ruolo={p.ruolo} />
      {!compact && <Avatar p={p} size={32} />}
      <div style={{flex:1,minWidth:0}}>
        <div style={{display:"flex",alignItems:"center",gap:5,overflow:"hidden"}}>
          <span style={{fontSize:11.5,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",textTransform:"uppercase",color:compact?M.txt:p.acq?M.acq:M.txt}}>{p.nome}</span>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:4,marginTop:0}}>
          {!compact&&<><span style={{color:M.txt3,fontSize:10}}>{calcAge(p.ddn)} anni</span><span style={{color:M.borderS}}>·</span></>}
          <span style={{color:M.txt3,fontSize:10}}>{p.naz||"—"}</span>
          <span style={{color:M.borderS}}>·</span>
          <span style={{color:M.txt3,fontSize:10}}>#{p.numeroMaglia||"—"}</span>
          <span style={{color:M.borderS}}>·</span>
          <span style={{fontSize:10,fontWeight:expiring||expiringNext?600:400,color:contractColor}}>{p.contratto}{expiring||expiringNext?" ⚠":""}</span>
        </div>
      </div>
    </div>
  );
}

/* ══════════ SECTION HEADER (list views) ══════════ */
function SectionHeader({ catId, count }) {
  const c = getCat(catId);
  const over  = c.max!=null && count>c.max;
  const under = c.min!=null && count<c.min;
  const cc    = over||under ? M.red : c.color;
  return (
    <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:8}}>
      <span style={{width:6,height:6,borderRadius:"50%",flexShrink:0,background:c.color,display:"inline-block",boxShadow:`0 0 5px ${c.color}60`}}/>
      <span style={{color:M.txt2,fontSize:10,fontWeight:700,letterSpacing:".1em",textTransform:"uppercase"}}>{c.label}</span>
      <span style={{background:M.raised,border:`1px solid ${M.border}`,color:cc,fontSize:10,fontWeight:700,padding:"0 6px",borderRadius:99}}>
        {count}{c.max!=null?`/${c.max}`:""}{c.min!=null&&c.max==null?` / min ${c.min}`:""}
      </span>
    </div>
  );
}

/* ══════════ PLAYER DETAIL (read-only modal) ══════════ */
function PlayerDetail({ player: p, onClose }) {
  const expiring = p.contratto <= CURR_YEAR;
  const loyalty  = loyaltyYrs(p.inRosaDa);
  const catSA    = getCat(p.cat);
  const catUEFA  = getCat(p.catUEFA);

  const InfoCell = ({ label, value, color }) => (
    <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between",gap:8,background:M.raised,border:`1px solid ${M.borderS}`,borderRadius:8,padding:"7px 10px"}}>
      <div style={{fontSize:8,fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",color:M.txt3}}>{label}</div>
      <div style={{fontSize:11,fontWeight:700,color:color??M.txt,textAlign:"right",filter:p.ignora&&["Contratto","In rosa da","Acquisto"].includes(label)?"blur(4px)":undefined}}>{value??<span style={{color:M.txt3}}>—</span>}</div>
    </div>
  );

  return (
    <div onClick={e=>e.target===e.currentTarget&&onClose()} style={{position:"fixed",inset:0,background:"rgba(7,10,18,.88)",backdropFilter:"blur(12px)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:200,padding:"16px"}}>
      <div style={{background:M.surface,border:`1px solid ${M.border}`,borderRadius:14,padding:"24px",width:460,maxWidth:"96vw",boxShadow:"0 24px 80px rgba(0,0,0,.8)",maxHeight:"92vh",overflowY:"auto"}}>

        {/* Header */}
        <div style={{display:"flex",gap:16,alignItems:"center",marginBottom:20}}>
          <Avatar p={p} size={64} />
          <div style={{flex:1,minWidth:0}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
              <span style={{fontSize:16,fontWeight:800,color:p.cat==="fuoriLista"?M.red:M.txt,textTransform:"uppercase"}}>{p.nome}</span>
              {p.cat==="fuoriLista"&&<Tag label="ESC" color={M.red}/>} 
              {p.inVendita&&<Tag label="VEN" color={M.red}/>} 
            </div>
            <div style={{display:"flex",flexWrap:"wrap",gap:6,alignItems:"center"}}>
              <RoleBadge ruolo={p.ruolo}/>
              <span style={{color:M.txt2,fontSize:11}}>#{p.numeroMaglia}</span>
              <span style={{color:M.txt3,fontSize:11}}>·</span>
              <span style={{color:M.txt2,fontSize:11}}>{p.naz}</span>
              {p.vivaioClub&&<Tag label="CLUB" color="#7B9FD4"/>}
              {p.vivaioItalia&&<Tag label="ITA" color="#4AADA8"/>}
            </div>
          </div>
          <div style={{marginBottom: "24px"}}>
            <button onClick={onClose} style={{background:"transparent",border:"none",color:M.txt3,cursor:"pointer",fontSize:22,lineHeight:1,padding:0,flexShrink:0}}>×</button>
          </div>
        </div>

        {/* Stats grid */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:6,marginBottom:6}}>
          <InfoCell label="Età"      value={`${calcAge(p.ddn)} anni`} />
          <InfoCell label="Altezza"  value={p.altezza?`${p.altezza} m`:null} />
          <InfoCell label="Piede"    value={p.piede} />
          <InfoCell label="Contratto" value={`${p.contratto}${expiring?" ⚠":""}`} color={expiring?M.amber:M.txt} />
          <InfoCell label="In rosa da" value={p.inRosaDa?p.inRosaDa:"Vivaio"} />
          <InfoCell label="Acquisto"  value={p.compratoA?`${p.compratoA} M€`:null} />
        </div>

        {/* Lista memberships */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
          {[{label:"LISTA SERIE A",cat:catSA},{label:"LISTA UEFA",cat:catUEFA}].map(({label,cat})=>(
            <div key={label} style={{background:M.raised,border:`1px solid ${M.borderS}`,borderRadius:8,padding:"10px 12px",filter:p.ignora?"blur(4px)":undefined}}>
              <div style={{fontSize:8.5,fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",color:M.txt3,marginBottom:6}}>{label}</div>
              <div style={{display:"flex",alignItems:"center",gap:5}}>
                <span style={{width:6,height:6,borderRadius:"50%",background:cat.color,display:"inline-block",flexShrink:0}}/>
                <span style={{fontSize:11,fontWeight:600,color:cat.color}}>{cat.label}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ══════════ ROSA VIEW (list tabs) ══════════ */
function RosaView({ players, catField, cats, onView, search, roleFilter }) {
  const bycat = (catId) => {
    const base = sortByRole(players.filter(p=>p[catField]===catId&&(!roleFilter||p.ruolo===roleFilter)));
    if (!search) return base;
    const q = search.toLowerCase();
    return base.filter(p=>p.nome.toLowerCase().includes(q)||p.naz.toLowerCase().includes(q));
  };
  const mainCat    = cats[0];
  const bottomCats = cats.slice(1,-1);
  const mainPl     = bycat(mainCat.id);
  const n=mainPl.length, sz=Math.ceil(n/3);
  const libCols = [mainPl.slice(0,sz), mainPl.slice(sz,sz*2), mainPl.slice(sz*2)];

  return (
    <div style={{flex:1,minWidth:0,display:"flex",flexDirection:"column",gap:18}}>
      <div>
        <SectionHeader catId={mainCat.id} count={players.filter(p=>p[catField]===mainCat.id).length} />
        {mainPl.length>0
          ? <div style={{display:"flex",gap:8}}>
              {libCols.map((col,ci)=>(
                <div key={ci} style={{flex:1,display:"flex",flexDirection:"column",gap:4}}>
                  {col.map(p=><PlayerRow key={p.id} p={p} onView={onView}/>)}
                </div>
              ))}
            </div>
          : <div style={{color:M.txt3,textAlign:"center",padding:"20px 0",fontSize:11,fontStyle:"italic"}}>Nessun giocatore assegnato</div>
        }
      </div>
      <div style={{display:"grid",gridTemplateColumns:`repeat(${bottomCats.length},1fr)`,gap:14}}>
        {bottomCats.map(c=>{
          const pl=bycat(c.id);
          return (
            <div key={c.id}>
              <SectionHeader catId={c.id} count={players.filter(p=>p[catField]===c.id).length}/>
              <div style={{display:"flex",flexDirection:"column",gap:4}}>
                {pl.map(p=><PlayerRow key={p.id} p={p} onView={onView}/>)}
              </div>
              {pl.length===0&&<div style={{color:M.txt3,textAlign:"center",padding:"12px 0",fontSize:10,fontStyle:"italic"}}>—</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ══════════ SIDEBOARD (list tabs) ══════════ */
function Sideboard({ players, tab, onView }) {
  const [open, setOpen] = useState(true);
  const isUEFA   = tab==="ucl";
  const cats     = isUEFA ? CAT_UEFA : CAT_SA;
  const catField = isUEFA ? "catUEFA" : "cat";
  const fuoriCat = isUEFA ? "fuoriListaUEFA" : "fuoriLista";
  const fuoriPl  = sortByRole(players.filter(p=>p[catField]===fuoriCat));
  const catStats = cats.map(c=>({...c,n:players.filter(p=>p[catField]===c.id).length}));
  const listaTotal = catStats.filter(c=>!["fuoriLista","fuoriListaUEFA","under22","listaBUEFA"].includes(c.id)).reduce((s,c)=>s+c.n,0);
  const listaMax = 25;
  const statCats = cats.filter(c=>c.id!==fuoriCat);
  const listaPlayers = players.filter(p=>p[catField]!==fuoriCat);
  const roleStats = ROLES.map(r=>({role:r,players:listaPlayers.filter(p=>p.ruolo===r)})).filter(s=>s.players.length>0);

  const barInfo=(c)=>{
    if(c.max!=null){const pct=Math.min((c.n/c.max)*100,100);return{pct,col:c.n>c.max?M.red:c.color,show:true};}
    if(c.min!=null){const pct=Math.min((c.n/c.min)*100,100);return{pct,col:c.n<c.min?M.amber:M.acq,show:true};}
    return{show:false};
  };

  return (
    <div style={{width:open?264:42,flexShrink:0,overflow:"hidden",transition:"width .28s cubic-bezier(.4,0,.2,1)",display:"flex"}}>
      <div style={{width:42,flexShrink:0,display:"flex",flexDirection:"column",alignItems:"center",paddingTop:2,gap:6}}>
        <button onClick={()=>setOpen(o=>!o)} style={{background:"transparent",border:`1px solid ${M.border}`,borderRadius:5,color:M.txt3,width:22,height:22,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
          <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
            {open?<path strokeLinecap="round" strokeLinejoin="round" d="M9 18l6-6-6-6"/>:<path strokeLinecap="round" strokeLinejoin="round" d="M15 18l-6-6 6-6"/>}
          </svg>
        </button>
        {!open&&catStats.map(c=>{
          const warn=(c.max&&c.n>c.max)||(c.min&&c.n<c.min);
          return <div key={c.id} title={`${c.label}: ${c.n}`} style={{width:28,background:M.surface,border:`1px solid ${M.border}`,borderRadius:6,padding:"4px 0",textAlign:"center",fontSize:10,fontWeight:700,color:warn?M.red:c.color}}>{c.n}</div>;
        })}
      </div>
      <div style={{width:222,flexShrink:0,opacity:open?1:0,transition:"opacity .18s",pointerEvents:open?"auto":"none",display:"flex",flexDirection:"column",gap:10}}>

        {/* STATS */}
        <div style={{background:M.surface,border:`1px solid ${M.borderS}`,borderRadius:10,padding:"13px 14px"}}>
          <div style={{fontSize:9,fontWeight:700,letterSpacing:".12em",textTransform:"uppercase",color:M.txt3,marginBottom:13}}>Slot lista {isUEFA?"UEFA":"Serie A"}</div>
          {catStats.map(c=>{const bi=barInfo(c);const warn=(c.max&&c.n>c.max)||(c.min&&c.n<c.min);return(
            <div key={c.id} style={{marginBottom:11}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                <span style={{fontSize:10.5,color:M.txt2}}>{c.label}</span>
                <span style={{fontSize:10.5,fontWeight:700,color:warn?M.red:c.color}}>{c.n}{c.max?`/${c.max}`:c.min?` / min ${c.min}`:""}</span>
              </div>
              {bi.show&&<div style={{height:3,background:M.raised,borderRadius:99,overflow:"hidden"}}><div style={{height:"100%",width:`${bi.pct}%`,background:bi.col,borderRadius:99,transition:"width .4s ease"}}/></div>}
            </div>
          );})}
          {isUEFA&&(()=>{const f=catStats.filter(c=>["clubUEFA","assocUEFA"].includes(c.id)).reduce((s,c)=>s+c.n,0);return(<div style={{borderTop:`1px solid ${M.borderS}`,paddingTop:10,marginTop:4}}><div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}><span style={{fontSize:10,color:M.txt3,fontWeight:600}}>Formati totale</span><span style={{fontSize:10.5,fontWeight:700,color:M.txt2}}>{f}/8</span></div><div style={{height:3,background:M.raised,borderRadius:99,overflow:"hidden"}}><div style={{height:"100%",width:`${Math.min(f/8*100,100)}%`,background:M.blue,borderRadius:99,transition:"width .4s ease"}}/></div></div>);})()} 
          {!isUEFA&&(()=>{const over=listaTotal;return(<div style={{borderTop:`1px solid ${M.borderS}`,paddingTop:10,marginTop:4}}><div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}><span style={{fontSize:10,color:M.txt3,fontWeight:600}}>Totale Over</span><span style={{fontSize:10.5,fontWeight:700,color:over>25?M.red:M.txt2}}>{over}/25</span></div><div style={{height:3,background:M.raised,borderRadius:99,overflow:"hidden"}}><div style={{height:"100%",width:`${Math.min(over/25*100,100)}%`,background:over>25?M.red:M.blue,borderRadius:99,transition:"width .4s ease"}}/></div></div>);})()} 
          {isUEFA&&<div style={{borderTop:`1px solid ${M.borderS}`,paddingTop:10,marginTop:4}}>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}><span style={{fontSize:10,color:M.txt3,fontWeight:600}}>Totale Lista A</span><span style={{fontSize:10.5,fontWeight:700,color:listaTotal>listaMax?M.red:M.txt2}}>{listaTotal}/{listaMax}</span></div>
            <div style={{height:3,background:M.raised,borderRadius:99,overflow:"hidden"}}><div style={{height:"100%",width:`${Math.min(listaTotal/listaMax*100,100)}%`,background:listaTotal>listaMax?M.red:M.blue,borderRadius:99,transition:"width .4s ease"}}/></div>
          </div>}
        </div>

        {/* COMPOSIZIONE PER RUOLO */}
        <div style={{background:M.surface,border:`1px solid ${M.borderS}`,borderRadius:10,padding:"13px 14px"}}>
          <div style={{fontSize:9,fontWeight:700,letterSpacing:".12em",textTransform:"uppercase",color:M.txt3,marginBottom:12}}>Composizione rosa</div>
          <div style={{display:"flex",justifyContent:"space-between",paddingBottom:9,marginBottom:10,borderBottom:`1px solid ${M.borderS}`}}>
            <span style={{fontSize:10.5,color:M.txt2}}>Giocatori in lista</span>
            <span style={{fontSize:10.5,fontWeight:700,color:M.txt2}}>{listaPlayers.length}</span>
          </div>
          {roleStats.map(s=>(
            <div key={s.role} style={{marginBottom:11}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:5}}>
                <div style={{display:"flex",alignItems:"center",gap:5}}><RoleBadge ruolo={s.role}/><span style={{fontSize:10.5,color:M.txt,fontWeight:700}}>{RL[s.role]}</span></div>
                <span style={{fontSize:11,fontWeight:800,color:M.txt}}>{s.players.length}</span>
              </div>
              <div style={{display:"flex",flexWrap:"wrap",gap:3}}>
                {statCats.map(c=>{const n=s.players.filter(p=>p[catField]===c.id).length;return n>0?(<span key={c.id} style={{fontSize:8.5,color:c.color,background:`${c.color}14`,border:`1px solid ${c.color}30`,borderRadius:4,padding:"2px 5px"}}>{c.label}: {n}</span>):null;})}
              </div>
            </div>
          ))}
        </div>

        {/* FUORI LISTA */}
        {fuoriPl.length>0&&(
          <div>
            <div style={{fontSize:9,fontWeight:700,letterSpacing:".12em",textTransform:"uppercase",color:M.txt3,marginBottom:8,marginTop:"1rem"}}>Fuori Lista</div>
            <div style={{display:"flex",flexDirection:"column",gap:4}}>
              {fuoriPl.map(p=><PlayerRow key={p.id} p={p} onView={onView} compact/>)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ══════════ DETTAGLIO ROSA ══════════ */
const INIT_FILTERS = { search:"", contratto:"tutti", piede:"tutti", vendita:false, vivaio:false, u22:false };

function DettaglioRosa({ players, onView }) {
  const [filters, setFilters] = useState(INIT_FILTERS);
  const setF = (k,v) => setFilters(f=>({...f,[k]:v}));

  const active = players;
  const filtered = active.filter(p=>{
    if (filters.search && !p.nome.toLowerCase().includes(filters.search.toLowerCase()) && !p.naz.toLowerCase().includes(filters.search.toLowerCase())) return false;
    if (filters.contratto==="corrente"&&p.contratto!==CURR_YEAR) return false;
    if (filters.contratto==="prossimo"&&p.contratto!==CURR_YEAR+1) return false;
    if (filters.piede!=="tutti"&&p.piede!==filters.piede) return false;
    if (filters.vendita&&!p.inVendita) return false;
    if (filters.vivaio&&!p.vivaio) return false;
    if (filters.u22&&p.cat!=="under22") return false;
    return true;
  });

  const SLK = { background:M.surface, border:`1px solid ${M.border}`, borderRadius:7, color:M.txt2, padding:"5px 9px", fontSize:10.5, outline:"none", fontFamily:"inherit", cursor:"pointer" };
  const TGL = (label, key) => (
    <button onClick={()=>setF(key,!filters[key])} style={{...SLK, color:filters[key]?M.acq:M.txt3, border:`1px solid ${filters[key]?M.acq+"60":M.border}`, background:filters[key]?`${M.acq}14`:M.surface}}>
      {label}
    </button>
  );

  return (
    <div style={CONTENT_FRAME_STYLE}>
      <div style={{flex:1,minWidth:0}}>

        {/* FILTER BAR */}
        <div style={{display:"flex",gap:7,flexWrap:"wrap",alignItems:"center",marginBottom:20,padding:"12px 14px",background:M.surface,border:`1px solid ${M.borderS}`,borderRadius:10}}>
          <div style={{position:"relative",flexShrink:0}}>
            <svg style={{position:"absolute",left:8,top:"50%",transform:"translateY(-50%)"}} width="11" height="11" fill="none" stroke={M.txt3} strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            <input value={filters.search} onChange={e=>setF("search",e.target.value)} placeholder="Cerca giocatore…"
              style={{...SLK,paddingLeft:24,width:150}} />
          </div>
          <select value={filters.contratto} onChange={e=>setF("contratto",e.target.value)} style={SLK}>
            <option value="tutti">Contratto</option>
            <option value="corrente">In scadenza ({CURR_YEAR})</option>
            <option value="prossimo">Entra in scadenza ({CURR_YEAR+1})</option>
          </select>
          <select value={filters.piede} onChange={e=>setF("piede",e.target.value)} style={SLK}>
            <option value="tutti">Piede</option>
            <option value="DX">Destro</option>
            <option value="SX">Sinistro</option>
          </select>
          <div style={{marginLeft:"auto",display:"flex",gap:7}}>
          {TGL("IN VENDITA","vendita")}
          {TGL("VIVAIO","vivaio")}
          {TGL("U22","u22")}
          </div>
        </div>

        {/* ROLE SECTIONS */}
        {ROLES.map(role=>{
          const rolePl  = filtered.filter(p=>p.ruolo===role);
          if (!rolePl.length) return null;
          const allRole = active.filter(p=>p.ruolo===role);
          const ages    = allRole.map(p=>calcAge(p.ddn)).filter(a=>typeof a==="number");
          const heights = allRole.filter(p=>p.altezza).map(p=>p.altezza);
          const invest  = allRole.reduce((s,p)=>s+p.compratoA+p,0);

          return (
            <div key={role} style={{marginBottom:24}}>
              {/* Role header */}
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8,paddingBottom:8,borderBottom:`1px solid ${M.borderS}`}}>
                <RoleBadge ruolo={role}/>
                <span style={{fontSize:12,fontWeight:700,color:M.txt}}>{RL[role]}</span>
                <span style={{fontSize:10,color:M.txt3}}>{allRole.length} giocatori</span>
                <div style={{display:"flex",gap:8,marginLeft:"auto"}}>
                  {ages.length>0&&<span style={{fontSize:9.5,color:M.txt3,textTransform:"uppercase",letterSpacing:".05em"}}>ETÀ <span style={{color:M.txt2,fontWeight:600,letterSpacing:0}}>{fmt1(avg(ages))}</span></span>}
                  {heights.length>0&&<span style={{fontSize:9.5,color:M.txt3,textTransform:"uppercase",letterSpacing:".05em"}}>ALTEZZA <span style={{color:M.txt2,fontWeight:600,letterSpacing:0}}>{fmt2(avg(heights))} m</span></span>}
                  {invest>0&&<span style={{fontSize:9.5,color:M.txt3,textTransform:"uppercase",letterSpacing:".05em"}}>INVESTIMENTO <span style={{color:M.txt2,fontWeight:600,letterSpacing:0}}>{invest.toFixed(1)} M€</span></span>}
                </div>
              </div>

              {/* Players */}
              <div style={{display:"flex",flexDirection:"column",gap:3}}>
                {rolePl.sort((a,b)=>a.nome.localeCompare(b.nome)).map(p=>(
                  <DettaglioRow key={p.id} p={p} onView={onView}/>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* DETTAGLIO SIDEBOARD */}
      <DettaglioSideboard players={active}/>
    </div>
  );
}

/* ══════════ DETTAGLIO ROW ══════════ */
function DettaglioRow({ p, onView }) {
  const [hov, setHov] = useState(false);
  const fuoriRosa = p.cat==="fuoriLista";
  const expiring = p.contratto<=CURR_YEAR;
  const expiringNext = p.contratto===CURR_YEAR+1;
  const loyalty = loyaltyYrs(p.inRosaDa);

  const Cell = ({value, color, width, align="left"}) => (
    <span style={{width,minWidth:width,fontSize:10.5,color:color??M.txt2,textAlign:align,flexShrink:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{value||"—"}</span>
  );

  return (
    <div
      onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      onClick={()=>onView(p)}
      style={{
        display:"flex",alignItems:"center",gap:10,cursor:"pointer",minWidth:0,
        background:hov?M.hover:(fuoriRosa?`${M.red}12`:p.inVendita?`${M.red}12`:p.acq?`${M.acq}10`:M.surface),
        borderRadius:8,padding:"6px 10px",
        border:`1px solid ${hov?M.border:M.borderS}`,
        transition:"background .1s",
      }}
    >
      <Avatar p={p} size={28}/>
      <div style={{display:"flex",alignItems:"center",gap:8,flex:1,minWidth:0,overflow:"hidden",whiteSpace:"nowrap"}}>
        <span style={{fontSize:11.5,fontWeight:600,color:fuoriRosa?M.red:p.inVendita?M.red:p.acq?M.acq:M.txt,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",textTransform:"uppercase"}}>{p.nome}</span>
        <span style={{color:M.txt3,fontSize:10,flexShrink:0}}>{p.naz||"—"}</span>
        <span style={{color:M.txt3,fontSize:10,flexShrink:0}}>{calcAge(p.ddn)} anni</span>
        <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",flexShrink:0}}>
        {fuoriRosa&&<Tag label="ESC" color={M.red}/>} 
        {p.inVendita&&<Tag label="VEN" color={M.red}/>} 
        {p.acq&&<Tag label="ACQ" color={M.acq}/>}
        {p.vivaioClub&&<Tag label="CLUB" color="#7B9FD4"/>}
        {p.vivaioItalia&&<Tag label="ITA" color="#4AADA8"/>}
        {p.vivaio&&!p.vivaioClub&&!p.vivaioItalia&&<Tag label="VIVAIO" color={M.amber}/>} 
        {p.cat==="under22"&&<Tag label="U22" color={M.amber}/>} 
        {p.catUEFA==="listaBUEFA"&&<Tag label="LIS. B" color={M.amber}/>} 
        </div>
      </div>
      <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
        <Cell value={`#${p.numeroMaglia}`} width={32} align="right"/>
        <Cell value={p.ruolo} width={28}/>
        <Cell value={p.piede} width={24}/>
        <Cell value={p.altezza?`${p.altezza}m`:null} width={42}/>
        <Cell value={p.contratto?`${p.contratto}${expiring?" ⚠":""}`:null} width={48} color={expiring?M.amber:expiringNext?M.blue:M.txt2}/>
        <Cell value={p.compratoA?`${p.compratoA}M`:null} width={38}/>
        <Cell value={loyalty===null?null:loyalty<=0?"nuovo":`${fmt1(loyalty)}y`} width={38}/>
      </div>
    </div>
  );
}

/* ══════════ DETTAGLIO SIDEBOARD ══════════ */
function DettaglioSideboard({ players }) {
  const [open, setOpen] = useState(true);

  const ages    = players.map(p=>calcAge(p.ddn)).filter(a=>typeof a==="number"&&a>0);
  const heights = players.filter(p=>p.altezza).map(p=>p.altezza);
  const loyals  = players.map(p=>loyaltyYrs(p.inRosaDa)).filter(l=>l!==null&&l>=0);
  const invest  = players.reduce((s,p)=>s+p.compratoA,0);
  const expiring= players.filter(p=>p.contratto===CURR_YEAR);
  const vivaio  = players.filter(p=>p.vivaio);
  const pctDX   = players.length ? Math.round(players.filter(p=>p.piede==="DX").length/players.length*100) : 0;
  const nazCount= players.reduce((acc,p)=>{
    if (!p.naz) return acc;
    return {...acc,[p.naz]:(acc[p.naz]??0)+1};
  },{});
  const nazioni = Object.keys(nazCount);
  const topNaz  = Object.entries(nazCount).sort((a,b)=>b[1]-a[1]).slice(0,5);

  const StatRow = ({label, value, sub}) => (
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline"}}>
      <span style={{fontSize:10.5,color:M.txt2}}>{label}</span>
      <div style={{textAlign:"right"}}>
        <span style={{fontSize:11,fontWeight:700,color:M.txt}}>{value}</span>
        {sub&&<span style={{fontSize:9,color:M.txt3,marginLeft:5}}>{sub}</span>}
      </div>
    </div>
  );

  const SectionTitle = ({children}) => (
    <div style={{fontSize:9,fontWeight:700,letterSpacing:".12em",textTransform:"uppercase",color:M.txt3,marginBottom:3,marginTop:10,paddingTop:9,borderTop:`1px solid ${M.borderS}`}}>
      {children}
    </div>
  );

  return (
    <div style={{width:open?264:42,flexShrink:0,overflow:"hidden",transition:"width .28s cubic-bezier(.4,0,.2,1)",display:"flex"}}>
      <div style={{width:42,flexShrink:0,display:"flex",flexDirection:"column",alignItems:"center",paddingTop:2,gap:6}}>
        <button onClick={()=>setOpen(o=>!o)} style={{background:"transparent",border:`1px solid ${M.border}`,borderRadius:5,color:M.txt3,width:22,height:22,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
          <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
            {open?<path strokeLinecap="round" strokeLinejoin="round" d="M9 18l6-6-6-6"/>:<path strokeLinecap="round" strokeLinejoin="round" d="M15 18l-6-6 6-6"/>}
          </svg>
        </button>
      </div>

      <div style={{width:222,flexShrink:0,opacity:open?1:0,transition:"opacity .18s",pointerEvents:open?"auto":"none",overflowY:"auto",maxHeight:"calc(100vh - 80px)"}}>
        <div style={{background:M.surface,border:`1px solid ${M.borderS}`,borderRadius:10,padding:"14px 15px"}}>
          <div style={{fontSize:9,fontWeight:700,letterSpacing:".12em",textTransform:"uppercase",color:M.txt3,marginBottom:12}}>Statistiche rosa</div>

          <StatRow label="Giocatori" value={players.length}/>
          <StatRow label="Media età" value={`${fmt1(avg(ages))}`}/>
          <StatRow label="Media altezza" value={`${avg(heights).toFixed(2)} m`}/>
          <StatRow label="Piede destro" value={`${pctDX}%`}/>

          <SectionTitle>Investimento</SectionTitle>
          <StatRow label="Totale" value={`${invest.toFixed(1)} M€`}/>
          <StatRow label="Media" value={`${(invest/Math.max(players.length,1)).toFixed(1)} M€`}/>
          <StatRow label="Solo vivaio" value={players.filter(p=>p.compratoA===0&&p.vivaio).length}/>

          <SectionTitle>Fedeltà</SectionTitle>
          <StatRow label="Media anni" value={`${fmt1(avg(loyals))} anni`}/>
          <StatRow label="Nuovi (26/27)" value={players.filter(p=>loyaltyYrs(p.inRosaDa)!==null&&loyaltyYrs(p.inRosaDa)<=1).length}/>
          <StatRow label="Vivaio" value={vivaio.length}/>

          <SectionTitle>Contratti in scadenza</SectionTitle>
          <div style={{marginTop:".5rem"}}>
            {expiring.map(p=>(<div key={p.id} style={{fontSize:10,color:M.txt,marginBottom:".25rem"}}>{p.nome} <span style={{color: M.txt2}}>({calcAge(p.ddn)} anni)</span></div>))}
          </div>

          <SectionTitle>Nazionalità · {nazioni.length}</SectionTitle>
          <div style={{marginTop:".5rem"}}>
            {topNaz.map(([naz,n])=>(
              <div key={naz} style={{display:"flex",alignItems:"center",gap:7,marginBottom:6}}>
                <span style={{fontSize:10,color:M.txt2,minWidth:28}}>{naz}</span>
                <div style={{flex:1,height:3,background:M.raised,borderRadius:99,overflow:"hidden"}}>
                  <div style={{height:"100%",width:`${(n/Math.max(players.length,1))*100}%`,background:M.blue,borderRadius:99}}/>
                </div>
                <span style={{fontSize:10,fontWeight:700,color:M.txt2,minWidth:12,textAlign:"right"}}>{n}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════ MERCATO ══════════ */
const money = (value) => `${Number(value ?? 0).toFixed(Number(value ?? 0) % 1 ? 1 : 0)} M€`;

function MarketPlayer({ player, label, color, onView, sale=false }) {
  const [hov, setHov] = useState(false);
  if (!player) return null;
  const expiring = player.contratto <= CURR_YEAR;
  const expiringNext = player.contratto === CURR_YEAR + 1;
  const tags = [
    player.vivaioClub && <Tag key="club" label="CLUB" color="#7B9FD4"/>,
    player.vivaioItalia && <Tag key="ita" label="ITA" color="#4AADA8"/>,
    player.vivaio && !player.vivaioClub && !player.vivaioItalia && <Tag key="vivaio" label="VIVAIO" color={M.amber}/>,
    player.cat === "under22" && <Tag key="u22" label="U22" color={M.amber}/>,
  ].filter(Boolean);
  return (
    <div onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)} onClick={()=>onView?.(player)} style={{background:hov?M.hover:M.surface,border:`1px solid ${hov?M.border:M.borderS}`,borderRadius:8,padding:"7px 9px",display:"flex",alignItems:"center",gap:8,cursor:onView?"pointer":"default",transition:"background .12s, border-color .12s"}}>
      <RoleBadge ruolo={player.ruolo}/>
      <Avatar p={player} size={32}/>
      <div style={{flex:1,minWidth:0}}>
        <div style={{display:"flex",alignItems:"center",gap:5,overflow:"hidden"}}>
          <span style={{fontSize:11.5,fontWeight:600,color:M.txt,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",textTransform:"uppercase"}}>{player.nome}</span>
          {!sale&&label&&<Tag label={label} color={color}/>} 
        </div>
        <div style={{display:"flex",alignItems:"center",gap:4,marginTop:0,whiteSpace:"nowrap"}}>
          <span style={{color:M.txt3,fontSize:10}}>{calcAge(player.ddn)} anni</span><span style={{color:M.borderS}}>·</span>
          <span style={{color:M.txt3,fontSize:10}}>{player.naz||"—"}</span>
          {!sale&&<><span style={{color:M.borderS}}>·</span><span style={{color:M.txt3,fontSize:10}}>#{player.numeroMaglia||"—"}</span><span style={{color:M.borderS}}>·</span><span style={{fontSize:10,color:expiring?M.amber:expiringNext?M.blue:M.txt3}}>{player.contratto}{expiring||expiringNext?" ⚠":""}</span></>}
        </div>
      </div>
      {!sale&&<div style={{display:"flex",gap:3,flexShrink:0}}>{tags}</div>}
    </div>
  );
}

function MarketOperation({ operation, player, type, onView }) {
  const isIncoming = type === "acquisto";
  const [hov, setHov] = useState(false);
  const tags = player ? [
    player.vivaioClub && <Tag key="club" label="CLUB" color="#7B9FD4"/>,
    player.vivaioItalia && <Tag key="ita" label="ITA" color="#4AADA8"/>,
    player.cat === "under22" && <Tag key="u22" label="U22" color={M.amber}/>,
  ].filter(Boolean) : [];
  return (
    <div onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)} onClick={()=>onView?.(player)} style={{background:hov?M.hover:M.surface,border:`1px solid ${hov?M.border:M.borderS}`,borderRadius:8,padding:"7px 9px",display:"flex",alignItems:"center",gap:8,cursor:player&&onView?"pointer":"default",transition:"background .12s, border-color .12s"}}>
      {player ? <RoleBadge ruolo={player.ruolo}/> : <div style={{width:28,flexShrink:0}}/>}
      {player ? <Avatar p={player} size={32}/> : <div style={{width:32,height:32,borderRadius:"50%",background:M.raised,flexShrink:0}}/>}
      <div style={{flex:1,minWidth:0}}>
        <div style={{display:"flex",alignItems:"baseline",gap:6,whiteSpace:"nowrap",overflow:"hidden"}}>
          <div style={{fontSize:11.5,fontWeight:600,color:M.txt,overflow:"hidden",textOverflow:"ellipsis",textTransform:"uppercase"}}>{player?.nome ?? `ID ${operation.id}`}</div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:4,marginTop:0,whiteSpace:"nowrap"}}>
          {player&&<><span style={{color:M.txt3,fontSize:10}}>{calcAge(player.ddn)} anni</span><span style={{color:M.borderS}}>·</span><span style={{color:M.txt3,fontSize:10}}>{player.naz||"—"}</span></>}
          <span style={{color:M.borderS}}>·</span><span style={{fontSize:9.5,color:M.txt3}}>{operation.tipologia.toUpperCase()}</span><span style={{color:M.borderS}}>{operation.club && `·`}</span><span style={{fontSize:9.5,color:M.txt3}}>{operation.club.toUpperCase()}</span>
        </div>
      </div>
      {tags.length>0&&<div style={{display:"flex",gap:3,flexShrink:0}}>{tags}</div>}
      <div style={{fontSize:11,fontWeight:700,color:isIncoming?M.acq:M.red,whiteSpace:"nowrap"}}>{money(operation.valore)}</div>
    </div>
  );
}

function MarketSection({ title, operations, playersById, type, onView, color=M.blue, empty="Nessuna operazione" }) {
  return (
    <section style={{flex:1,minWidth:0}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
        <span style={{display:"flex",alignItems:"center",gap:7,fontSize:10,fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",color:M.txt2}}><span style={{width:6,height:6,borderRadius:"50%",background:color,display:"inline-block",boxShadow:`0 0 5px ${color}60`}}/>{title}</span>
        <span style={{fontSize:10,color:M.txt3}}>{operations.length}</span>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:5}}>
        {operations.map((operation,index)=><MarketOperation key={`${operation.id}-${operation.anno}-${index}`} operation={operation} player={playersById.get(String(operation.id))} type={type} onView={onView}/>)}
        {!operations.length&&<div style={{padding:"16px 0",fontSize:10,color:M.txt3,fontStyle:"italic"}}>{empty}</div>}
      </div>
    </section>
  );
}

function MercatoView({ players, market, onView }) {
  const [statsOpen, setStatsOpen] = useState(true);
  const playersById = new Map(players.map(player=>[String(player.id),player]));
  const acquisti = market?.acquisti ?? [];
  const possibiliAcquisti = (market?.["possibili-acquisti"] ?? []).map((candidate,index)=>({
    player: normalizePlayer({...candidate,id:`rumor-${index}`}, `rumor-${index}`),
    operation: { id:`rumor-${index}`, club:candidate.club, valore:candidate.valore, tipologia:candidate.tipologia ?? "possibile acquisto" },
  }));
  const cessioni = market?.cessioni ?? [];
  const cessioniAnno = cessioni.filter(operation=>operation.anno === CURR_YEAR - 1);
  const cessioniPrecedenti = cessioni.filter(operation=>operation.anno < CURR_YEAR - 1);
  const altriProventi = cessioniPrecedenti.filter(operation=>Number(operation.valore||0) > 0);
  const esclusi = players.filter(player=>player.cat === "fuoriLista" && !player.ignora);
  const possibiliCessioni = [
    ...players.filter(player=>player.inVendita && !player.ignora),
    ...esclusi.filter(player=>!player.inVendita),
  ];
  const spesa = acquisti.reduce((sum,operation)=>sum+Number(operation.valore||0),0);
  const usciteAnno = cessioniAnno.reduce((sum,operation)=>sum+Number(operation.valore||0),0);
  const ricaviPrecedenti = cessioniPrecedenti.reduce((sum,operation)=>sum+Number(operation.valore||0),0);
  const ruoloStats = ROLES.map(role=>({
    role,
    entrate: acquisti.filter(operation=>playersById.get(String(operation.id))?.ruolo===role).reduce((sum,operation)=>sum+Number(operation.valore||0),0),
    uscite: cessioniAnno.filter(operation=>playersById.get(String(operation.id))?.ruolo===role).reduce((sum,operation)=>sum+Number(operation.valore||0),0),
  }));
  const totaleMercato = ricaviPrecedenti + usciteAnno - spesa;

  return (
    <div style={CONTENT_FRAME_STYLE}>
      <div style={{flex:1,minWidth:0,display:"flex",flexDirection:"column",gap:22}}>
          <div style={{display:"flex",gap:28}}>
            <div style={{flex:1,minWidth:0}}>
              <MarketSection title="Cessioni" operations={cessioniAnno} playersById={playersById} type="cessione" onView={onView} color={M.red}/>
              <div style={{marginTop:18}}><MarketSection title="Altri proventi" operations={altriProventi} playersById={playersById} type="cessione" onView={onView} color={M.amber}/></div>
            </div>
            <MarketSection title="Acquisti" operations={acquisti} playersById={playersById} type="acquisto" onView={onView} color={M.acq}/>
          </div>
        <div style={{borderTop:`1px solid ${M.borderS}`,paddingTop:16}}>
          <div style={{display:"flex",gap:28}}>
            <section style={{flex:1,minWidth:0}}>
              <div style={{display:"flex",alignItems:"center",gap:7,fontSize:10,fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",color:M.txt2,marginBottom:8}}><span style={{width:6,height:6,borderRadius:"50%",background:M.red,display:"inline-block",boxShadow:`0 0 5px ${M.red}60`}}/>Possibili cessioni</div>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>{possibiliCessioni.map(player=><MarketPlayer key={player.id} player={player} label={player.inVendita?"VEN":"ESC"} color={M.red} onView={onView} sale={player.inVendita}/>)}</div>
              {!possibiliCessioni.length&&<div style={{padding:"16px 0",fontSize:10,color:M.txt3,fontStyle:"italic"}}>Nessun giocatore segnalato</div>}
            </section>
            <section style={{flex:1,minWidth:0}}>
              <div style={{display:"flex",alignItems:"center",gap:7,fontSize:10,fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",color:M.txt2,marginBottom:8}}><span style={{width:6,height:6,borderRadius:"50%",background:M.acq,display:"inline-block",boxShadow:`0 0 5px ${M.acq}60`}}/>Possibili acquisti</div>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {possibiliAcquisti.map(({player,operation})=><MarketOperation key={operation.id} operation={operation} player={player} type="acquisto"/>)}
                {!possibiliAcquisti.length&&<div style={{padding:"10px 0",fontSize:10,color:M.txt3,fontStyle:"italic"}}>Nessuna indiscrezione disponibile</div>}
              </div>
            </section>
          </div>
        </div>
      </div>
      <div style={{width:statsOpen?264:42,flexShrink:0,overflow:"hidden",transition:"width .28s cubic-bezier(.4,0,.2,1)",display:"flex"}}>
        <div style={{width:42,flexShrink:0,display:"flex",flexDirection:"column",alignItems:"center",paddingTop:2}}>
          <button onClick={()=>setStatsOpen(open=>!open)} title={statsOpen?"Chiudi pannello":"Apri pannello"} style={{background:"transparent",border:`1px solid ${M.border}`,borderRadius:5,color:M.txt3,width:22,height:22,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
              {statsOpen?<path strokeLinecap="round" strokeLinejoin="round" d="M9 18l6-6-6-6"/>:<path strokeLinecap="round" strokeLinejoin="round" d="M15 18l-6-6 6-6"/>}
            </svg>
          </button>
        </div>
        <div style={{width:222,flexShrink:0,opacity:statsOpen?1:0,transition:"opacity .18s",pointerEvents:statsOpen?"auto":"none"}}>
          <div style={{background:M.surface,border:`1px solid ${M.borderS}`,borderRadius:10,padding:"13px 14px"}}>
          <div style={{fontSize:9,fontWeight:700,letterSpacing:".12em",textTransform:"uppercase",color:M.txt3,marginBottom:10}}>Statistiche mercato</div>
          <div style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:`1px solid ${M.borderS}`}}><span style={{fontSize:10.5,color:M.txt2}}>Totale</span><b style={{fontSize:11,color:M.txt}}>{money(totaleMercato)}</b></div>
          <div style={{display:"flex",justifyContent:"space-between",padding:"8px 0 4px"}}><span style={{fontSize:10.5,color:M.txt2}}>Acquisti</span><b style={{fontSize:11,color:M.acq}}>{money(spesa)}</b></div>
          <div style={{display:"flex",justifyContent:"space-between",padding:"4px 0"}}><span style={{fontSize:10.5,color:M.txt2}}>Cessioni</span><b style={{fontSize:11,color:M.red}}>{money(usciteAnno)}</b></div>
          <div style={{display:"flex",justifyContent:"space-between",padding:"4px 0"}}><span style={{fontSize:10.5,color:M.txt2}}>Altri proventi</span><b style={{fontSize:11,color:M.txt}}>{money(ricaviPrecedenti)}</b></div>
          <div style={{fontSize:9,fontWeight:700,letterSpacing:".12em",textTransform:"uppercase",color:M.txt3,marginTop:18,marginBottom:8}}>Spesa per ruolo</div>
          {ruoloStats.map(stat=><div key={stat.role} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"2px 0"}}><span style={{fontSize:10.5,fontWeight:500,color:M.txt}}>{RL[stat.role]}</span><span style={{fontSize:9.5,color:M.txt3}}>E <b style={{color:M.acq}}>{money(stat.entrate)}</b> · U <b style={{color:M.red}}>{money(stat.uscite)}</b></span></div>)}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════
   APP
══════════════════════════════════ */
export default function App() {
  const [players,  setPlayers]  = useState([]);
  const [market,   setMarket]   = useState(null);
  const [ready,    setReady]    = useState(false);
  const [selected, setSelected] = useState(null);
  const [tab,      setTab]      = useState("dettaglio");
  const [search,   setSearch]   = useState("");
  const [roleFilter, setRoleFilter] = useState("");

  useEffect(()=>{
    const l=document.createElement("link");
    l.rel="stylesheet";
    l.href="https://fonts.googleapis.com/css2?family=Geist+Mono:wght@300;400;500;600;700;800&display=swap";
    document.head.appendChild(l);
  },[]);

  useEffect(()=>{
    (async()=>{
      try{
        const response = await fetch("/rosa-26-27.json");
        const data = await response.json();
        setPlayers(data.map(normalizePlayer));
        const marketResponse = await fetch("/mercato-26-27.json");
        setMarket(await marketResponse.json());
      }catch(e){ console.warn("JSON non caricato:", e); }
      setReady(true);
    })();
  },[]);

  const isList = tab==="serieA"||tab==="ucl";
  const visiblePlayers = players.filter(player=>!player.ignora);

  return (
    <div style={{minHeight:"100vh",background:M.bg,fontFamily:"'Geist Mono','Courier New',monospace",color:M.txt}}>

      {/* TAB BAR */}
      <div style={{position:"relative",height:50,boxSizing:"border-box",borderBottom:`1px solid ${M.borderS}`,padding:"0 22px",display:"flex",alignItems:"flex-end"}}>
        <div style={{display:"flex"}}>
          {TABS.map(t=>(
            <button key={t.id} onClick={()=>setTab(t.id)} style={{background:"transparent",border:"none",borderBottom:tab===t.id?`2px solid ${M.blue}`:"2px solid transparent",color:tab===t.id?M.blue:M.txt3,padding:"13px 18px 11px",cursor:"pointer",fontSize:11,fontWeight:700,letterSpacing:".05em",fontFamily:"inherit",transition:"color .1s"}}>{t.label}</button>
          ))}
        </div>

        {isList&&(
          <div style={{position:"absolute",right:22,top:0,height:"100%",display:"flex",gap:7,alignItems:"center"}}>
            <select value={roleFilter} onChange={e=>setRoleFilter(e.target.value)} style={{background:M.surface,border:`1px solid ${M.border}`,borderRadius:7,color:roleFilter?M.txt2:M.txt3,padding:"6px 8px",fontSize:11,outline:"none",fontFamily:"inherit",cursor:"pointer"}}>
              <option value="">Ruolo</option>
              {ROLES.map(r=><option key={r} value={r}>{r}</option>)}
            </select>
            <div style={{position:"relative"}}>
              <svg style={{position:"absolute",left:9,top:"50%",transform:"translateY(-50%)"}} width="12" height="12" fill="none" stroke={M.txt3} strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Cerca…" style={{background:M.surface,border:`1px solid ${M.border}`,borderRadius:7,color:M.txt2,padding:"6px 10px 6px 28px",fontSize:11,outline:"none",fontFamily:"inherit",width:160}}/>
            </div>
          </div>
        )}
      </div>

      {/* CONTENT */}
      <div style={{width:"100%",maxWidth:1920,boxSizing:"border-box",margin:"0 auto"}}>
        {tab==="serieA"&&(
          <div style={CONTENT_FRAME_STYLE}>
            <RosaView players={visiblePlayers} catField="cat" cats={CAT_SA} onView={setSelected} search={search} roleFilter={roleFilter}/>
            <Sideboard players={visiblePlayers} tab="serieA" onView={setSelected}/>
          </div>
        )}
        {tab==="ucl"&&(
          <div style={CONTENT_FRAME_STYLE}>
            <RosaView players={visiblePlayers} catField="catUEFA" cats={CAT_UEFA} onView={setSelected} search={search} roleFilter={roleFilter}/>
            <Sideboard players={visiblePlayers} tab="ucl" onView={setSelected}/>
          </div>
        )}
        {tab==="dettaglio"&&<DettaglioRosa players={visiblePlayers} onView={setSelected}/>} 
        {tab==="mercato"&&<MercatoView players={players} market={market} onView={setSelected}/>} 
        {/*tab==="squad"&&<SquadBuildr players={visiblePlayers}/>*/} 
      </div>

      {/* READ-ONLY DETAIL */}
      {selected&&<PlayerDetail player={selected} onClose={()=>setSelected(null)}/>}
    </div>
  );
}